// src/pages/MatchesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Sparkles, MapPin, Lock, UserCircle, MessageCircle, Eye, Clock } from 'lucide-react';
import userService from '../services/userService';
import { getDisplayAge, getCommonInterests } from '../utils';
import { INTERESTS } from '../constants';
import { Btn, Card, Badge, Loading, EmptyState, ErrorState, Infobulle } from '../components/ui';
import ProfileModal from '../modals/ProfileModal';

const TABS = [
  { id: 'matches', icon: Sparkles },
  { id: 'likes', icon: Heart },
  { id: 'received', icon: Eye },
];

const MatchesPage = ({ user, openAuth, toast, onUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'matches');
  const [matches, setMatches] = useState([]);
  const [likedProfiles, setLikedProfiles] = useState([]);
  const [receivedLikes, setReceivedLikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);

  const tabConfig = {
    matches: {
      label: t('matches.tabs.matches'),
      emptyTitle: t('matches.empty.matchesTitle'),
      emptyDesc: t('matches.empty.matchesDesc'),
    },
    likes: {
      label: t('matches.tabs.likes'),
      emptyTitle: t('matches.empty.likesTitle'),
      emptyDesc: t('matches.empty.likesDesc'),
    },
    received: {
      label: t('matches.tabs.received'),
      emptyTitle: t('matches.empty.receivedTitle'),
      emptyDesc: t('matches.empty.receivedDesc'),
    },
  };

  const handleUnlike = async (id) => {
    // Optimistic : retirer immédiatement du state local
    setLikedProfiles((prev) => prev.filter((p) => p.uid !== id));
    setSelected(null);

    const r = await userService.like(user.uid, id);
    if (r.success) {
      toast(r.msg, 'success');
      // Rafraîchir le user complet (likes[], matches[])
      const fresh = await userService.getUser();
      if (fresh) {
        if (onUpdate) onUpdate(fresh);
        if ((fresh.matches || []).length !== (user.matches || []).length) {
          setMatches(await userService.getMatches(fresh.uid));
        }
      }
    } else {
      // Rollback : recharger les listes
      console.error('handleUnlike failed:', r);
      toast(t('matches.toast.error'), 'error');
      load();
    }
  };

  const handleLikeBack = async (targetUid) => {
    const r = await userService.like(user.uid, targetUid);
    if (r.success) {
      toast(r.msg, 'success');
      const fresh = await userService.getUser();
      if (fresh) {
        if (onUpdate) onUpdate(fresh);
        // Refresh lists
        load();
      }
    } else {
      toast(t('matches.toast.error'), 'error');
    }
  };

  const load = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);

      // Charger les matches
      const m = await userService.getMatches(user.uid);
      setMatches(m);

      // Charger les profils likés (excluant matches et bloqués)
      const likeIds = user.likes || [];
      const userBlocked = user.blocked || [];
      const liked = [];
      for (const id of likeIds) {
        if ((user.matches || []).includes(id)) continue;
        if (userBlocked.includes(id)) continue;
        const p = await userService.getProfile(id);
        if (p && !(p.blocked || []).includes(user.uid)) liked.push(p);
      }
      setLikedProfiles(liked);

      // Charger les likes reçus
      const rawReceived = await userService.getReceivedLikes(user.uid);
      const receivedProfiles = [];
      for (const rl of rawReceived) {
        const profile = await userService.getProfile(rl.fromUid);
        if (profile) {
          receivedProfiles.push({
            ...profile,
            _receivedAt: rl.createdAt,
            _isMatch: (user.matches || []).includes(rl.fromUid),
          });
        }
      }
      setReceivedLikes(receivedProfiles);
    } catch (err) {
      console.error('MatchesPage load error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
  }, [user, load]);

  if (!user) {
    return (
      <div className="pt-32 pb-32 container mx-auto px-4">
        <Card className="max-w-md mx-auto p-6 sm:p-8 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-3 sm:mb-4">
            <Lock size={22} className="sm:hidden text-rose-500" />
            <Lock size={28} className="hidden sm:block text-rose-500" />
          </div>
          <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-3 dark:text-gray-100">
            {t('matches.guest.title')}
          </h2>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-4 sm:mb-6">
            {t('matches.guest.desc')}
          </p>
          <Btn onClick={openAuth} className="w-full" data-testid="matches-btn-auth">
            {t('matches.guest.cta')}
          </Btn>
        </Card>
      </div>
    );
  }

  const likeCount = (user.likes || []).length;
  const currentList = tab === 'matches' ? matches : tab === 'likes' ? likedProfiles : receivedLikes;
  const currentTab = tabConfig[tab];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-100 dark:from-rose-950/[0.35] via-rose-50/50 dark:via-rose-950/[0.15] to-rose-50/10 dark:to-transparent">
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <Helmet>
          <title>{t('matches.meta.title')}</title>
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="https://hmonghearts.com/matches" />
        </Helmet>
        <div className="mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-2xl font-bold dark:text-gray-100">{t('matches.title')}</h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
            {t('matches.subtitle', {
              matchCount: matches.length,
              matchPlural: matches.length > 1 ? 'es' : '',
              likeCount: likeCount,
              likePlural: likeCount > 1 ? 's' : '',
            })}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl mb-4 sm:mb-6 overflow-x-auto">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              data-testid={`matches-tab-${tb.id}`}
              onClick={() => setTab(tb.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 rounded-xl text-sm font-semibold transition whitespace-nowrap ${
                tab === tb.id
                  ? 'bg-white dark:bg-gray-700 text-rose-600 dark:text-rose-400 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <tb.icon size={16} />
              {tabConfig[tb.id].label}
              {tb.id === 'matches' && matches.length > 0 && (
                <span className="bg-rose-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {matches.length}
                </span>
              )}
              {tb.id === 'received' && receivedLikes.length > 0 && (
                <span className="bg-rose-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {receivedLikes.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <Infobulle type="info" storageKey="hmh-hint-matches">
          {t('matches.hint')}
        </Infobulle>

        {/* Content */}
        {error ? (
          <ErrorState onRetry={load} />
        ) : loading ? (
          <Loading msg={t('common.loading')} />
        ) : currentList.length === 0 ? (
          <EmptyState
            icon={TABS.find((tb) => tb.id === tab).icon}
            title={currentTab.emptyTitle}
            desc={currentTab.emptyDesc}
            ctaLabel={
              tab === 'received' ? t('matches.received.emptyCta') : t('matches.discoverProfiles')
            }
            onCtaClick={() =>
              (window.location.href = tab === 'received' ? '/profile' : '/discover')
            }
          />
        ) : (
          <>
            {tab === 'matches' && (
              <button
                onClick={() => navigate('/messages')}
                className="w-full bg-gradient-to-r from-rose-50 to-purple-50 dark:from-rose-950/30 dark:to-purple-950/30 rounded-2xl p-4 mb-4 text-left hover:from-rose-100 hover:to-purple-100 dark:hover:from-rose-950/50 dark:hover:to-purple-950/50 transition-colors"
              >
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <MessageCircle size={14} className="inline text-purple-500 mr-1" />
                  {t('matches.mutualBanner')}
                </p>
              </button>
            )}

            {tab === 'received' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('matches.received.count', { count: receivedLikes.length })}
              </p>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {currentList.map((p, index) => {
                const common = getCommonInterests(user, p, INTERESTS).slice(0, 2);
                const isReceivedTab = tab === 'received';
                const isMatch = isReceivedTab && p._isMatch;
                const shouldBlur = isReceivedTab && !isMatch;
                const isNew =
                  isReceivedTab &&
                  p._receivedAt &&
                  Date.now() - (p._receivedAt.seconds || 0) * 1000 < 86400000;
                return (
                  <Card
                    key={p.uid}
                    data-testid="matches-card"
                    className="overflow-hidden group animate-fade-in-up"
                    style={{ animationDelay: `${index * 60}ms` }}
                    hover
                    onClick={() => !shouldBlur && setSelected(p)}
                  >
                    <div className="relative h-56 sm:h-72 md:h-80 rounded-xl overflow-hidden">
                      {p.profilePicture ? (
                        <img
                          src={p.profilePicture}
                          alt={p.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          style={shouldBlur ? { filter: 'blur(8px)' } : {}}
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                          <UserCircle size={56} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                      {/* Overlay like-back pour les cards floutées */}
                      {shouldBlur && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLikeBack(p.uid);
                            }}
                            data-testid="matches-btn-likeback"
                            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-gradient-to-r from-rose-500 to-fuchsia-500 text-white text-xs sm:text-sm font-semibold rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                          >
                            <Heart size={18} className="fill-current" />
                            {t('matches.received.likeBack')}
                          </button>
                        </div>
                      )}

                      {/* Bouton unlike (onglet Likes envoyés) */}
                      {tab === 'likes' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnlike(p.uid);
                          }}
                          data-testid="matches-btn-unlike"
                          className="absolute top-2 right-2 z-10 bg-white/90 dark:bg-gray-900/90 rounded-full p-2 hover:bg-rose-50 dark:hover:bg-rose-950/50 transition shadow-sm"
                          aria-label={t('matches.unlike')}
                        >
                          <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />
                        </button>
                      )}

                      {/* Badge received */}
                      {isReceivedTab && (
                        <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex gap-1.5">
                          {isMatch ? (
                            <Badge c="green">
                              <Sparkles size={12} className="mr-1" />
                              {t('matches.received.matchBadge')}
                            </Badge>
                          ) : isNew ? (
                            <Badge c="yellow">
                              <Clock size={12} className="mr-1" />
                              {t('matches.received.new')}
                            </Badge>
                          ) : null}
                        </div>
                      )}

                      {p.online && (
                        <span className="absolute top-4 right-4 w-2.5 h-2.5 bg-emerald-500 rounded-full shadow-sm" />
                      )}

                      <div className="absolute bottom-0 p-3 sm:p-5 text-white w-full">
                        {p.clan && <Badge c="rose">{p.clan}</Badge>}
                        {!shouldBlur && common.length > 0 && (
                          <div className="hidden sm:flex gap-1.5 mt-1.5">
                            {common.map((c) => (
                              <span
                                key={c.value}
                                className="text-xs bg-white/20 px-2 py-0.5 rounded-full"
                              >
                                {c.labelKey ? t(c.labelKey) : c.value}
                              </span>
                            ))}
                          </div>
                        )}
                        <h3 className="text-base sm:text-2xl font-bold mt-1 sm:mt-2 truncate">
                          {p.name}
                          {getDisplayAge(p) !== null ? `, ${getDisplayAge(p)}` : ''}
                        </h3>
                        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-white/80">
                          <MapPin size={12} className="sm:hidden" />
                          <MapPin size={14} className="hidden sm:block" />
                          <span className="truncate">{p.city}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        <ProfileModal
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          profile={selected}
          context="dating"
          onLike={() => handleUnlike(selected?.uid)}
          hasLiked={selected ? (user.likes || []).includes(selected.uid) : false}
          toast={toast}
          currentUser={user}
          onBlock={(blockedId) => {
            setSelected(null);
            setMatches((prev) => prev.filter((p) => p.uid !== blockedId));
            setLikedProfiles((prev) => prev.filter((p) => p.uid !== blockedId));
            setReceivedLikes((prev) => prev.filter((p) => p.uid !== blockedId));
          }}
        />
      </div>
    </div>
  );
};

export default MatchesPage;
